import { useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { FiUploadCloud, FiDownload } from "react-icons/fi";
import { useValidateSchemeProductCodesMutation } from "../../../features/tenant/scheme/schemeApi";
import { readSpreadsheetRows, downloadCsvFile } from "../../../utils/spreadsheet";

const MAX_CODES = 20000;

/**
 * Upload a CSV/XLSX of product codes (column `product_code`) and merge the valid ones
 * into the scheme's selected products.
 *
 * Props:
 *  - selectedProducts: current selection [{ product_id, product_code, name, ... }]
 *  - applyProducts(list): parent replaces the selection (it handles SKU-points cleanup)
 */
const ProductCodeUpload = ({ selectedProducts, applyProducts }) => {
  const fileRef = useRef(null);
  const [validateCodes, { isLoading }] = useValidateSchemeProductCodesMutation();
  const [result, setResult] = useState(null); // { valid, inactive, unknown, duplicates, total }
  const [mode, setMode] = useState("append");

  const tenantId = JSON.parse(sessionStorage.getItem("vendorData"))?.tenant_id;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const rows = await readSpreadsheetRows(file);
      if (rows.length === 0) return toast.error("The file is empty");
      const first = rows[0];
      const col = "product_code" in first ? "product_code" : Object.keys(first)[0];
      if (!col) return toast.error("No columns found in the file");
      const codes = rows.map((r) => String(r[col] ?? "").trim()).filter(Boolean);
      if (codes.length === 0) return toast.error(`No product codes found in column "${col}"`);
      if (codes.length > MAX_CODES) return toast.error(`Too many rows (max ${MAX_CODES})`);

      const res = await validateCodes({
        token: sessionStorage.getItem("access_token"),
        tenant_id: tenantId,
        codes,
      }).unwrap();
      setResult(res.body);
      setMode(selectedProducts.length > 0 ? "append" : "replace");
    } catch (err) {
      toast.error(err?.data?.message || "Could not read / validate the file");
    }
  };

  const apply = () => {
    if (!result?.valid?.length) return;
    const incoming = result.valid.map((p) => ({
      product_id: p.product_id,
      product_code: p.product_code,
      name: p.name || "",
      category_id: p.category_id,
      category_name: p.category_name,
    }));
    if (mode === "replace") {
      applyProducts(incoming);
    } else {
      const have = new Set(selectedProducts.map((p) => p.product_code));
      applyProducts([...selectedProducts, ...incoming.filter((p) => !have.has(p.product_code))]);
    }
    toast.success(`${incoming.length} product code(s) ${mode === "replace" ? "set" : "added"}`);
    setResult(null);
  };

  const preview = (list) => list.slice(0, 20).join(", ") + (list.length > 20 ? ` … +${list.length - 20} more` : "");

  return (
    <div className="mb-4">
      <div className="flex flex-wrap items-center gap-2">
        <input ref={fileRef} type="file" accept=".csv,.xls,.xlsx" className="hidden" onChange={handleFile} />
        <button
          type="button"
          disabled={isLoading}
          onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <FiUploadCloud /> {isLoading ? "Validating…" : "Upload product codes (CSV/XLSX)"}
        </button>
        <button
          type="button"
          onClick={() => downloadCsvFile("scheme_product_codes_template.csv", ["product_code"], [["SKU-001"], ["SKU-002"]])}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs text-blue-700 hover:underline"
        >
          <FiDownload /> Template
        </button>
      </div>

      {result && (
        <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm">
          <div className="flex flex-wrap gap-x-5 gap-y-1">
            <span className="text-green-700 font-medium">{result.valid.length} valid</span>
            <span className={result.unknown.length ? "text-red-600 font-medium" : "text-gray-500"}>{result.unknown.length} not found</span>
            <span className={result.inactive.length ? "text-amber-600 font-medium" : "text-gray-500"}>{result.inactive.length} inactive</span>
            <span className="text-gray-500">{result.duplicates.length} duplicate(s) in file</span>
          </div>
          {result.unknown.length > 0 && <p className="mt-2 text-xs text-red-600 break-words">Not found: {preview(result.unknown)}</p>}
          {result.inactive.length > 0 && <p className="mt-1 text-xs text-amber-700 break-words">Inactive (skipped): {preview(result.inactive)}</p>}

          {result.valid.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-4">
              {selectedProducts.length > 0 && (
                <>
                  <label className="inline-flex items-center gap-1.5 text-xs text-gray-700">
                    <input type="radio" checked={mode === "append"} onChange={() => setMode("append")} /> Append to {selectedProducts.length} selected
                  </label>
                  <label className="inline-flex items-center gap-1.5 text-xs text-gray-700">
                    <input type="radio" checked={mode === "replace"} onChange={() => setMode("replace")} /> Replace selection
                  </label>
                </>
              )}
              <button type="button" onClick={apply} className="px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium">
                Use {result.valid.length} product(s)
              </button>
              <button type="button" onClick={() => setResult(null)} className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-xs text-gray-600">
                Cancel
              </button>
            </div>
          )}
          {result.valid.length === 0 && (
            <button type="button" onClick={() => setResult(null)} className="mt-3 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-xs text-gray-600">
              Close
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductCodeUpload;
